import {
  Alert,
  Anchor,
  Autocomplete,
  Badge,
  Button,
  Card,
  Checkbox,
  Container,
  createTheme,
  defaultVariantColorsResolver,
  Drawer,
  MantineThemeOverride,
  Menu,
  Modal,
  NavLink,
  PinInput,
  rem,
  Select,
  Textarea,
  TextInput,
  Title,
  Tooltip,
  VariantColorsResolver,
} from '@mantine/core';

import alertClassNames from './Alert.module.css';
import anchorClassNames from './Anchor.module.css';
import autocompleteClassNames from './Autocomplete.module.css';
import avatarClassNames from './Avatar.module.css';
import badgeClassNames from './Badge.module.css';
import boxClassNames from './Box.module.css';
import buttonClassNames from './Button.module.css';
import cardClassNames from './Card.module.css';
import checkboxClassNames from './Checkbox.module.css';
import drawerClassNames from './Drawer.module.css';
import fieldsetClassNames from './Fieldset.module.css';
import menuClassNames from './Menu.module.css';
import modalClassNames from './Modal.module.css';
import navLinkClassNames from './NavLink.module.css';
import notificationClassNames from './Notification.module.css';
import paperClassNames from './Paper.module.css';
import pinInputClassNames from './PinInput.module.css';
import selectClassNames from './Select.module.css';
import tabsClassNames from './Tabs.module.css';
import textClassNames from './Text.module.css';
import textareaClassNames from './Textarea.module.css';
import textInputClassNames from './TextInput.module.css';
import titleClassNames from './Title.module.css';
import tooltipClassNames from './Tooltip.module.css';

// Semantic colors defined in OKLCH with perceptually-uniform lightness differences (maps to figma colors)
// Reference colors:
// - primary: oklch(34.18% 0.0484 330.99) - Sway Purple II (lighter purple)
// - secondary: oklch(90.52% 0.0182 142.82) - Sway Secondary Green
// - background: oklch(96.72% 0 0) - White/near-white
// - text: oklch(19.35% 0.0491 332.66) - Sway Purple (darker purple)

// NEW COLORS, APR 2025
// SWAY BLACKCURRENT #2A1130 oklch(0.23 0.0655 319.57)
// SECONDARY VIOLET #8448CA oklch(0.54 0.1936 301.37)
// URGENT TERRACOTTA #D45C30 oklch(0.62 0.1623 39.59)
// SWAY WHITE #F4F4F4 oklch(0.97 0 0)
// INPUT FIELD LIGHT ##D6D6D6 oklch(0.88 0 0)

// These semantic colors allow us to change brand colors without updating references throughout the app
export const semanticColors = {
  // Main brand colors
  primary: [
    'oklch(97%   0.011   300)',
    'oklch(95.5% 0.018   300)',
    'oklch(91%   0.041   300)', // primary.2
    'oklch(85%   0.076   300)',
    'oklch(77%   0.129   300)',
    'oklch(68%   0.186   300)',
    'oklch(54%   0.1943  300)', // primary.6 - lighter purple
    'oklch(46%   0.165   300)',
    'oklch(34%   0.121   300)',
    'oklch(20%   0.07    300)', // primary.9 - darkest purple
  ] as const,

  // Neutral/background colors
  neutral: [
    'oklch(97%   0 0)',
    'oklch(95.5% 0 0)',
    'oklch(91%   0 0)',
    'oklch(85%   0 0)',
    'oklch(77%   0 0)',
    'oklch(68%   0 0)',
    'oklch(54%   0 0)',
    'oklch(46%   0 0)',
    'oklch(34%   0 0)',
    'oklch(20%   0 0)',
  ] as const,

  // Success colors (Green)
  success: [
    'oklch(98% 0.02 145)', // success.0 - Lightest tint
    'oklch(92% 0.03 145)', // success.1
    'oklch(86% 0.05 145)', // success.2
    'oklch(79% 0.07 145)', // success.3
    'oklch(73% 0.09 145)', // success.4
    'oklch(67% 0.11 145)', // success.5 - Base success color
    'oklch(60% 0.13 145)', // success.6
    'oklch(52% 0.12 145)', // success.7
    'oklch(44% 0.11 145)', // success.8
    'oklch(35% 0.10 145)', // success.9 - Darkest shade
  ] as const,

  // Danger/error colors (Red)
  danger: [
    'oklch(98% 0.03 25)', // danger.0 - Lightest tint
    'oklch(92% 0.06 25)', // danger.1
    'oklch(85% 0.09 25)', // danger.2
    'oklch(80% 0.12 25)', // danger.3
    'oklch(75% 0.16 25)', // danger.4
    'oklch(68% 0.20 25)', // danger.5 - Base danger color
    'oklch(60% 0.22 25)', // danger.6
    'oklch(52% 0.20 25)', // danger.7
    'oklch(44% 0.18 25)', // danger.8
    'oklch(35% 0.15 25)', // danger.9 - Darkest shade
  ] as const,

  // Warning colors (Orange/Amber)
  warning: [
    'oklch(98% 0.03 75)', // warning.0 - Lightest tint
    'oklch(93% 0.06 75)', // warning.1
    'oklch(88% 0.09 75)', // warning.2
    'oklch(83% 0.12 75)', // warning.3
    'oklch(78% 0.15 75)', // warning.4
    'oklch(73% 0.18 75)', // warning.5 - Base warning color
    'oklch(65% 0.20 75)', // warning.6
    'oklch(58% 0.18 75)', // warning.7
    'oklch(50% 0.16 75)', // warning.8
    'oklch(42% 0.12 75)', // warning.9 - Darkest shade
  ] as const,

  // Info colors (Blue)
  info: [
    'oklch(98% 0.03 250)', // info.0 - Lightest tint
    'oklch(92% 0.05 250)', // info.1
    'oklch(86% 0.08 250)', // info.2
    'oklch(80% 0.10 250)', // info.3
    'oklch(74% 0.12 250)', // info.4
    'oklch(68% 0.14 250)', // info.5 - Base info color
    'oklch(60% 0.15 250)', // info.6
    'oklch(50% 0.14 250)', // info.7
    'oklch(40% 0.12 250)', // info.8
    'oklch(30% 0.10 250)', // info.9 - Darkest shade
  ] as const,
};

// Spacing values
const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '28px',
  xl: '56px',
};

// Custom variant color resolver to support our semantic color variants
const variantColorResolver: VariantColorsResolver = (input) => {
  // Get default colors first
  const defaultResolvedColors = defaultVariantColorsResolver(input);

  // Add support for custom "danger" variant
  if (input.variant === 'danger') {
    return {
      background: 'var(--mantine-color-danger-5)',
      hover: 'var(--mantine-color-danger-7)',
      color: 'var(--mantine-color-neutral-0)',
      border: 'none',
    };
  }

  // Add support for custom "secondary" variant
  if (input.variant === 'secondary') {
    return {
      background: 'var(--mantine-color-primary-6)',
      hover: 'var(--mantine-color-primary-6)',
      color: 'var(--mantine-color-primary-9)',
      border: 'none',
    };
  }

  // Return default colors for standard variants
  return defaultResolvedColors;
};

export const mantineTheme: MantineThemeOverride = createTheme({
  colors: semanticColors,
  primaryColor: 'primary',
  primaryShade: 7, // Use reference color as primary
  variantColorResolver, // Add our custom variant color resolver

  breakpoints: {
    xs: '374px',
    sm: '768px',
    md: '992px',
    lg: '1200px',
    xl: '1408px',
  },

  // Font settings
  fontFamily: 'var(--font-manrope), system-ui, sans-serif',
  fontSizes: {
    sm: '12px',
    md: '16px',
    lg: '22px',
    xl: '28px',
  },

  // Global spacing
  spacing,

  headings: {
    fontFamily: 'var(--font-manrope), system-ui, sans-serif',
    textWrap: 'pretty',
    sizes: {
      h1: {
        fontWeight: '700',
        fontSize: '36px',
      },
      h2: {
        fontWeight: '600',
        fontSize: '28px',
      },
      h3: {
        fontWeight: '600',
        fontSize: '22px',
      },
    },
  },

  // Border radius settings
  defaultRadius: 'md',
  radius: {
    xs: '2px',
    sm: '5px',
    md: '10px',
    lg: '15px',
    xl: '20px',
  },

  // Set default values for components
  components: {
    // Button styling with CSS modules
    Button: Button.extend({
      classNames: buttonClassNames,
    }),

    // Badge styling with CSS modules
    Badge: Badge.extend({
      classNames: badgeClassNames,
    }),

    // Form inputs with CSS modules - actual styling in globals.css
    TextInput: TextInput.extend({
      classNames: textInputClassNames,
      defaultProps: {
        radius: 'md',
        size: 'md',
      },
    }),

    Modal: Modal.extend({
      classNames: modalClassNames,
    }),

    Drawer: Drawer.extend({
      classNames: drawerClassNames,
    }),

    Anchor: Anchor.extend({
      classNames: anchorClassNames,
    }),

    Menu: Menu.extend({
      classNames: menuClassNames,
    }),

    NavLink: NavLink.extend({
      classNames: navLinkClassNames,
    }),

    Select: Select.extend({
      classNames: selectClassNames,
    }),

    Card: Card.extend({
      classNames: cardClassNames,
    }),

    Alert: Alert.extend({
      classNames: alertClassNames,
    }),

    Tooltip: Tooltip.extend({
      classNames: tooltipClassNames,
    }),

    Textarea: Textarea.extend({
      classNames: textareaClassNames,
      defaultProps: {
        radius: 'md',
        size: 'md',
        minRows: 4,
      },
    }),

    Title: Title.extend({
      classNames: titleClassNames,
      defaultProps: {
        order: 1,
      },
    }),

    Autocomplete: Autocomplete.extend({
      classNames: autocompleteClassNames,
    }),

    // Paper (card) styling
    Paper: {
      defaultProps: {
        bg: 'var(--mantine-color-neutral-0)',
        p: 'lg',
      },
      classNames: paperClassNames,
    },

    // Avatar
    Avatar: {
      classNames: avatarClassNames,
    },

    Fieldset: {
      classNames: fieldsetClassNames,
    },

    // PinInput with CSS modules - actual styling in globals.css
    PinInput: PinInput.extend({
      classNames: pinInputClassNames,
      defaultProps: {
        radius: 'md',
        size: 'md',
      },
    }),

    // Text variants
    Text: {
      classNames: textClassNames,
    },

    // Checkbox with form module styles
    Checkbox: Checkbox.extend({
      classNames: checkboxClassNames,
      defaultProps: {
        radius: 'sm',
      },
    }),

    // Custom Box variants for layouts
    Box: {
      classNames: boxClassNames,
    },

    // Container with custom sizes
    Container: Container.extend({
      defaultProps: {
        px: 'md',
        py: 'lg',
      },
      vars: (_, { size, fluid }) => {
        // Custom container sizes (in px)
        const CONTAINER_SIZES = {
          xs: 540,
          sm: 720,
          md: 800, // Reduced from default ~1000px to 800px (80%)
          lg: 960,
          xl: 1140,
        };

        return {
          root: {
            '--container-size': fluid
              ? '100%'
              : size !== undefined && size in CONTAINER_SIZES
                ? rem(CONTAINER_SIZES[size as keyof typeof CONTAINER_SIZES])
                : rem((size as number) || 0),
          },
        };
      },
    }),

    // Stack (vertical spacing)
    Stack: {
      defaultProps: {
        spacing: 'md',
      },
    },

    // Group (horizontal spacing)
    Group: {
      defaultProps: {
        spacing: 'md',
      },
    },

    // Notifications styling - configure only with styles, don't recreate the component
    Notification: {
      classNames: notificationClassNames,
    },

    Tabs: {
      classNames: tabsClassNames,
    },
  },

  // Additional custom properties
  other: {
    backgroundColor: 'var(--mantine-color-neutral-0)', // background
    textColor: 'var(--mantine-color-primary-7)', // primary text
    borderColor: 'var(--mantine-color-neutral-4)', // border
    spacing,
  },
});
